import { memo } from 'react'
import { Avatar, Box, Card, CardContent, Typography } from '@mui/material'
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Badge as CertificationIcon,
} from '@mui/icons-material'
import { EMSID, VerificationStatus } from 'src/v3/core/types'
import { SelectedUser } from '../types'

interface UserProfileCardProps {
  user: SelectedUser
  emsID: EMSID
  verificationEnabled: boolean
  verificationStatus?: VerificationStatus
}

export let UserProfileCard = memo(({ user, emsID }: UserProfileCardProps) => {
  let getInitials = (firstName: string, lastName: string) =>
    `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()

  let formatAddress = () => {
    if (!user?.address && (!user?.city || !user?.state || !user?.zip))
      return null

    let addressPart = user?.address ? [user.address] : []
    let cityStatePart =
      user?.city && user?.state && user?.zip
        ? [`${user.city}, ${user.state} ${user.zip}`]
        : []

    return [...addressPart, ...cityStatePart].join(', ')
  }

  return (
    <Card className="mb-6">
      <CardContent className="p-8">
        <Box className="grid items-center grid-cols-1 gap-6 lg:grid-cols-12">
          <Box className="grid place-items-center lg:col-span-2 lg:place-items-start">
            {user?.imageURL ? (
              <Avatar
                src={user.imageURL}
                sx={{ width: 120, height: 120 }}
                alt={`Profile photo of ${user?.firstName} ${user?.lastName}`}
              />
            ) : (
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  fontSize: '2rem',
                  bgcolor: 'primary.main',
                }}
                aria-label={`Initials for ${user?.firstName} ${user?.lastName}`}
              >
                {user?.firstName && user?.lastName
                  ? getInitials(user.firstName, user.lastName)
                  : '?'}
              </Avatar>
            )}
          </Box>
          <Box className="lg:col-span-10">
            <Typography variant="h5" className="mb-2 font-semibold">
              {`${user?.firstName || ''} ${user?.lastName || ''}`.trim()}
            </Typography>
            <Box className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
              <Box className="grid grid-cols-1 gap-2">
                {user?.primaryEmail && (
                  <Box className="grid grid-cols-[auto_1fr] gap-2 items-center">
                    <EmailIcon
                      sx={{ fontSize: 18, color: 'text.secondary' }}
                      aria-hidden="true"
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      aria-label={`Email address: ${user?.primaryEmail}`}
                    >
                      {user?.primaryEmail}
                    </Typography>
                  </Box>
                )}
                {user?.phone && (
                  <Box className="grid grid-cols-[auto_1fr] gap-2 items-center">
                    <PhoneIcon
                      sx={{ fontSize: 18, color: 'text.secondary' }}
                      aria-hidden="true"
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      aria-label={`Phone number: ${user.phone}`}
                    >
                      {user.phone}
                    </Typography>
                  </Box>
                )}
                {formatAddress() && (
                  <Box className="grid grid-cols-[auto_1fr] gap-2 items-center">
                    <LocationIcon
                      sx={{ fontSize: 18, color: 'text.secondary' }}
                      aria-hidden="true"
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      aria-label={`Address: ${formatAddress()}`}
                    >
                      {formatAddress()}
                    </Typography>
                  </Box>
                )}
              </Box>

              {emsID && (
                <Box className="grid grid-cols-1 gap-2">
                  <Box className="grid grid-cols-[auto_1fr] gap-2 items-center">
                    <CertificationIcon
                      sx={{ fontSize: 18, color: 'text.secondary' }}
                      aria-hidden="true"
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      aria-label={`EMS ID: ${emsID.emsidNumber}`}
                    >
                      <strong>EMS ID:</strong> {emsID.emsidNumber}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
})

export default UserProfileCard
